import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowLeft,
  Globe,
  Clock,
  Activity,
  RefreshCw,
  Pencil,
  Trash2,
  ExternalLink,
  Copy,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServerDetail = ({ servers, groups, onEditServer, onDeleteServer, onCheckServer, onBack }) => {
  const { serverId } = useParams();
  const [uptimeData, setUptimeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHours, setSelectedHours] = useState(24);

  const server = servers.find((s) => s.id === serverId);
  const group = server ? groups.find((g) => g.id === server.group_id) : null;

  useEffect(() => {
    const fetchUptime = async () => {
      if (!serverId) return;
      
      setLoading(true);
      try {
        const response = await axios.get(`${API}/uptime/${serverId}?hours=${selectedHours}`);
        setUptimeData(response.data);
      } catch (error) {
        console.error("Failed to fetch uptime data:", error);
        toast.error("Failed to load uptime history");
      }
      setLoading(false);
    };

    fetchUptime();
  }, [serverId, selectedHours]);

  if (!server) {
    return (
      <div className="p-6 lg:p-8">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Servers
        </Button>
        <div className="text-center py-16">
          <h2 className="text-xl font-heading font-bold">Server not found</h2>
          <p className="text-muted-foreground mt-2">
            The server you're looking for doesn't exist or has been deleted.
          </p>
        </div>
      </div>
    );
  }

  const statusColors = {
    online: "status-online",
    offline: "status-offline",
    warning: "status-warning",
    unknown: "status-unknown",
  };

  const formatResponseTime = (ms) => {
    if (ms === null || ms === undefined) return "—";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatUptime = (percentage) => {
    if (percentage === null || percentage === undefined) return "—";
    return `${percentage.toFixed(2)}%`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Never";
    return new Date(timestamp).toLocaleString();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  // Process chart data
  const chartData = uptimeData
    .filter((check) => check.response_time !== null)
    .map((check) => ({
      time: new Date(check.checked_at).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      }),
      responseTime: check.response_time,
      status: check.status,
    }))
    .slice(-100); // Limit to last 100 points for performance

  // Calculate stats from uptime data
  const totalChecks = uptimeData.length;
  const onlineChecks = uptimeData.filter((c) => c.status === "online").length;
  const avgResponseTime = uptimeData.length > 0
    ? Math.round(
        uptimeData
          .filter((c) => c.response_time)
          .reduce((sum, c) => sum + c.response_time, 0) /
        uptimeData.filter((c) => c.response_time).length
      )
    : null;

  return (
    <div className="p-6 lg:p-8" data-testid="server-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            {/* Favicon */}
            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center shrink-0 overflow-hidden">
              {server.favicon_url ? (
                <img
                  src={server.favicon_url}
                  alt=""
                  className="w-8 h-8 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <Globe className="w-6 h-6 text-muted-foreground" />
              )}
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold">{server.name}</h1>
              {group && (
                <div className="flex items-center gap-1.5 mt-1">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: group.color }}
                  />
                  <span className="text-sm text-muted-foreground">{group.name}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => onCheckServer(server.id)}
            data-testid="check-server-detail-btn"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Check Now
          </Button>
          <Button
            variant="outline"
            onClick={() => onEditServer(server)}
            data-testid="edit-server-detail-btn"
          >
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </Button>
        </div>
      </div>

      {/* Status and Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Status</div>
            <Badge variant="outline" className={`${statusColors[server.status]} text-sm`}>
              {server.status === "online" && <CheckCircle className="mr-1 h-3.5 w-3.5" />}
              {server.status === "offline" && <XCircle className="mr-1 h-3.5 w-3.5" />}
              {server.status === "unknown" && <AlertCircle className="mr-1 h-3.5 w-3.5" />}
              {server.status.charAt(0).toUpperCase() + server.status.slice(1)}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Uptime (24h)</div>
            <div className={`text-2xl font-heading font-bold ${
              server.uptime_percentage >= 99 ? 'text-emerald-600 dark:text-emerald-400' :
              server.uptime_percentage >= 95 ? 'text-amber-600 dark:text-amber-400' :
              server.uptime_percentage ? 'text-rose-600 dark:text-rose-400' : ''
            }`}>
              {formatUptime(server.uptime_percentage)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Avg Response</div>
            <div className="text-2xl font-heading font-bold font-mono">
              {formatResponseTime(avgResponseTime)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Last Checked</div>
            <div className="text-sm font-medium">
              {formatDate(server.last_checked)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column - Response time chart */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-heading">Response Time</CardTitle>
                <div className="flex gap-2">
                  {[6, 12, 24, 48].map((hours) => (
                    <Button
                      key={hours}
                      variant={selectedHours === hours ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => setSelectedHours(hours)}
                      data-testid={`hours-btn-${hours}`}
                    >
                      {hours}h
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : chartData.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No response time data available
                </div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="responseGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="time"
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                        tickLine={false}
                      />
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                        tickLine={false}
                        tickFormatter={(value) => `${value}ms`}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                        labelStyle={{ color: "hsl(var(--foreground))" }}
                        formatter={(value) => [`${value}ms`, "Response Time"]}
                      />
                      <Area
                        type="monotone"
                        dataKey="responseTime"
                        stroke="hsl(var(--primary))"
                        fill="url(#responseGradient)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent checks */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Recent Checks</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                {uptimeData.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    No checks recorded yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {uptimeData.slice(-20).reverse().map((check, index) => (
                      <div
                        key={check.id || index}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          {check.status === "online" ? (
                            <CheckCircle className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-rose-500" />
                          )}
                          <div>
                            <code className="text-xs text-muted-foreground">
                              {check.address_url}
                            </code>
                            {check.error && (
                              <p className="text-xs text-rose-500 mt-0.5">{check.error}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-sm">
                          <div className="font-mono">
                            {check.response_time ? `${check.response_time}ms` : "—"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(check.checked_at).toLocaleTimeString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Right column - Details */}
        <div className="space-y-6">
          {/* Addresses */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Addresses</CardTitle>
            </CardHeader>
            <CardContent>
              {server.addresses?.length === 0 ? (
                <div className="text-center text-muted-foreground py-4">
                  No addresses configured
                </div>
              ) : (
                <div className="space-y-3">
                  {server.addresses?.map((address) => (
                    <div
                      key={address.id}
                      className="p-3 rounded-lg bg-muted/50 group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          {address.label && (
                            <div className="text-sm font-medium mb-1">
                              {address.label}
                            </div>
                          )}
                          <code className="text-xs text-muted-foreground break-all">
                            {address.url}
                          </code>
                        </div>
                        <div className="flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => copyToClipboard(address.url)}
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            asChild
                          >
                            <a
                              href={address.url.startsWith('http') ? address.url : `https://${address.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {server.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {server.notes}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span>{formatDate(server.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Last Updated</span>
                <span>{formatDate(server.updated_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Checks ({selectedHours}h)</span>
                <span>{totalChecks}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Successful Checks</span>
                <span className="text-emerald-600 dark:text-emerald-400">{onlineChecks}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ServerDetail;
